/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { authApi } from "./api";
import { EyeIcon, EyeOffIcon } from "../icons/EyeIcons";
import "./LoginModal.css";

export default function LoginModal({ open, onClose, onAuthenticated, onSwitchToRegister }) {
  // 'password' = phone + password form (default)
  // 'otp'      = phone-only form that triggers WhatsApp OTP
  // 'verify'   = 6-digit OTP entry
  const [stage, setStage] = useState("password");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(60);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (!open) {
      setStage("password");
      setPhone("");
      setPassword("");
      setShowPassword(false);
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (stage !== "verify" || resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [stage, resendTimer]);

  if (!open) return null;

  // ----- password login (the default) -----
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }
    if (!password) {
      setError("Please enter your password");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.login({ phone_number: phone, password });
      localStorage.setItem("access_token", res.access_token);
      localStorage.setItem("refresh_token", res.refresh_token);
      localStorage.setItem("user", JSON.stringify(res.user));
      onAuthenticated && onAuthenticated(res.user);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ----- OTP login: send the OTP, then go to verify stage -----
  const handleOtpRequest = async (e) => {
    e.preventDefault();
    setError("");
    if (phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.loginOtp({ phone_number: phone });
      setStage("verify");
      setResendTimer(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ----- OTP entry helpers -----
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKey = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").slice(0, 6);
    if (!/^\d+$/.test(text)) return;
    const next = [...otp];
    text.split("").forEach((c, i) => {
      if (i < 6) next[i] = c;
    });
    setOtp(next);
    const idx = Math.min(text.length, 5);
    otpRefs.current[idx]?.focus();
  };

  const handleVerify = async () => {
    setError("");
    const code = otp.join("");
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      const res = await authApi.verifyOtp({ phone_number: phone, otp_code: code });
      localStorage.setItem("access_token", res.access_token);
      localStorage.setItem("refresh_token", res.refresh_token);
      localStorage.setItem("user", JSON.stringify(res.user));
      onAuthenticated && onAuthenticated(res.user);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    setError("");
    try {
      await authApi.loginOtp({ phone_number: phone });
      setOtp(["", "", "", "", "", ""]);
      setResendTimer(60);
    } catch (err) {
      setError(err.message);
    }
  };

  const headerText = {
    password: "Login to Vishal Sanjivini MACS",
    otp: "Sign in with WhatsApp OTP",
    verify: "Verify your phone number",
  }[stage];

  return (
    <div className="vs-login-overlay" onClick={onClose}>
      <div className="vs-login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vs-login-header">
          <h2>{headerText}</h2>
          <button className="vs-login-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ===== Password login (default) ===== */}
        {stage === "password" && (
          <>
            <form onSubmit={handlePasswordLogin} className="vs-login-form">
              <div className="vs-field">
                <label htmlFor="vs-phone">Phone Number <span className="vs-required">*</span></label>
                <div className="vs-phone-row">
                  <span className="vs-phone-prefix">+91</span>
                  <input
                    id="vs-phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>
              </div>

              <div className="vs-field">
                <label htmlFor="vs-password">Password <span className="vs-required">*</span></label>
                <div className="vs-password-row">
                  <input
                    id="vs-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    className="vs-password-toggle"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                  </button>
                </div>
              </div>

              {error && <div className="vs-error">{error}</div>}

              <button type="submit" className="vs-btn-primary" disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <button
              type="button"
              className="vs-link vs-link-block"
              onClick={() => {
                setError("");
                setStage("otp");
              }}
            >
              Forgot password? Sign in with WhatsApp OTP →
            </button>

            <div className="vs-login-divider"><span>Don't have an account?</span></div>
            <button
              type="button"
              className="vs-btn-secondary vs-btn-fullwidth"
              onClick={() => {
                onClose();
                onSwitchToRegister && onSwitchToRegister();
              }}
            >
              Register Now
            </button>
          </>
        )}

        {/* ===== OTP-only login (request) ===== */}
        {stage === "otp" && (
          <>
            <p className="vs-otp-subtitle">
              We'll send a one-time passcode to your registered WhatsApp number.
            </p>
            <form onSubmit={handleOtpRequest} className="vs-login-form">
              <div className="vs-field">
                <label htmlFor="vs-phone-otp">Phone Number <span className="vs-required">*</span></label>
                <div className="vs-phone-row">
                  <span className="vs-phone-prefix">+91</span>
                  <input
                    id="vs-phone-otp"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>
              </div>

              {error && <div className="vs-error">{error}</div>}

              <button type="submit" className="vs-btn-primary" disabled={submitting}>
                {submitting ? "Sending OTP…" : "Send OTP"}
              </button>
            </form>

            <button
              type="button"
              className="vs-link vs-link-block"
              onClick={() => {
                setError("");
                setStage("password");
              }}
            >
              ← Back to password sign-in
            </button>
          </>
        )}

        {/* ===== OTP verification ===== */}
        {stage === "verify" && (
          <div className="vs-otp-wrap">
            <p className="vs-otp-subtitle">We've sent a WhatsApp OTP to verify your number</p>
            <div className="vs-otp-phone">
              <div className="vs-otp-phone-label">Phone Number</div>
              <div className="vs-otp-phone-value">+91 {phone}</div>
              <button
                type="button"
                className="vs-link"
                onClick={() => setStage("otp")}
              >
                Change Number
              </button>
            </div>

            <div className="vs-field">
              <label>Enter 6-digit OTP</label>
              <div className="vs-otp-inputs">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKey(i, e)}
                    onPaste={handleOtpPaste}
                  />
                ))}
              </div>
            </div>

            <div className="vs-otp-resend">
              <p>Didn't receive OTP?</p>
              {resendTimer > 0 ? (
                <p className="vs-muted">Resend OTP in {resendTimer}s</p>
              ) : (
                <button type="button" className="vs-link" onClick={resendOtp}>
                  Resend OTP
                </button>
              )}
            </div>

            {error && <div className="vs-error">{error}</div>}

            <div className="vs-otp-actions">
              <button type="button" className="vs-btn-secondary" onClick={() => setStage("otp")}>
                Back
              </button>
              <button
                type="button"
                className="vs-btn-primary"
                onClick={handleVerify}
                disabled={otp.join("").length !== 6 || submitting}
              >
                {submitting ? "Verifying…" : "Verify & Sign in"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
